import { getEventsForCountry, getCountries } from '@/lib/data-source';
import { CALENDAR_YEAR } from '@/lib/config';
import { isPublicHoliday } from '@/lib/event-filters';
import { toDateString, addDays, daysBetween } from '@/lib/date-calc';
import type { CalendarEvent, CountryCode } from '@/lib/types';

const YEAR = CALENDAR_YEAR;

/**
 * Weekend model per country.
 * Most countries: Sat-Sun (days 0,6). Some use Fri-Sat (days 5,6).
 * Egypt historically used Fri-Sun but currently uses Fri-Sat.
 */
type WeekendModel = [number, number]; // two weekend day numbers (0=Sun..6=Sat)

const COUNTRY_WEEKENDS: Partial<Record<CountryCode, WeekendModel>> = {
  AE: [5, 6], // Fri-Sat
  AF: [5, 6], // Fri-Sat
  SA: [5, 6], // Fri-Sat
  IQ: [5, 6], // Fri-Sat
  EG: [5, 6], // Fri-Sat
  SD: [5, 6], // Fri-Sat
  YE: [5, 6], // Fri-Sat
  SY: [5, 6], // Fri-Sat
};

function getWeekendDays(countryCode?: CountryCode): WeekendModel {
  if (countryCode && COUNTRY_WEEKENDS[countryCode]) return COUNTRY_WEEKENDS[countryCode];
  return [0, 6]; // default Sat-Sun
}

function isWeekendDay(dow: number, weekendDays: WeekendModel): boolean {
  return dow === weekendDays[0] || dow === weekendDays[1];
}

/** Get the workdays adjacent to the weekend for bridge-day logic */
function getWeekendAdjacentWorkdays(weekendDays: WeekendModel): { before: number; after: number } {
  // For Sat-Sun (6,0): Fri(5) before, Mon(1) after
  // For Fri-Sat (5,6): Thu(4) before, Sun(0) after
  const [a, b] = weekendDays;
  // Detect wrap-around (Sat=6 + Sun=0)
  if ((a === 0 && b === 6) || (a === 6 && b === 0)) {
    return { before: 5, after: 1 }; // Fri before Sat, Mon after Sun
  }
  const sorted = [a, b].sort((x, y) => x - y);
  const before = (sorted[0] - 1 + 7) % 7;
  const after = (sorted[1] + 1) % 7;
  return { before, after };
}

export interface LongWeekend {
  holiday: CalendarEvent;
  totalDaysOff: number;
  leaveDaysNeeded: number;
  startDate: string;
  endDate: string;
  bridgeDays: string[];
}

/**
 * Compute long weekends from public holidays.
 * Uses country-specific weekend model (Sat-Sun for most, Fri-Sat for MENA).
 * Checks day-of-week proximity to weekends:
 * - Adjacent to weekend → natural 3-day weekend (0 leave)
 * - One workday gap → take 1 bridge day (4 days off)
 * - Two workday gap → take 2 bridge days (5 days off)
 */
export function getLongWeekends(events: CalendarEvent[], countryCode?: CountryCode): LongWeekend[] {
  const publicHolidays = events.filter(isPublicHoliday);
  const weekendDays = getWeekendDays(countryCode);
  const { before: dayBeforeWeekend, after: dayAfterWeekend } = getWeekendAdjacentWorkdays(weekendDays);

  // All dates covered by a public holiday — a "bridge day" that lands on one is
  // already a day off, not leave you take. (almanak-omjj)
  const holidayDates = new Set<string>();
  for (const h of publicHolidays) {
    for (let c = new Date(h.dateStart + 'T00:00:00'); c <= new Date(h.dateEnd + 'T00:00:00'); c = addDays(c, 1)) {
      holidayDates.add(toDateString(c));
    }
  }

  const results: LongWeekend[] = [];

  for (const holiday of publicHolidays) {
    const d = new Date(holiday.dateStart + 'T00:00:00');
    const dow = d.getDay(); // 0=Sun, 1=Mon...6=Sat

    // Skip if already a weekend day
    if (isWeekendDay(dow, weekendDays)) continue;

    let startDate: Date;
    let endDate: Date;
    let bridgeDays: string[] = [];
    let leaveDaysNeeded = 0;

    if (dow === dayAfterWeekend) {
      // Day right after weekend — natural long weekend (Weekend + this day)
      // e.g., Mon for Sat-Sun, Sun for Fri-Sat
      startDate = addDays(d, -2); // go back to first weekend day
      endDate = d;
      leaveDaysNeeded = 0;
    } else if (dow === dayBeforeWeekend) {
      // Day right before weekend — natural long weekend (this day + Weekend)
      // e.g., Fri for Sat-Sun, Thu for Fri-Sat
      startDate = d;
      endDate = addDays(d, 2); // go forward to last weekend day
      leaveDaysNeeded = 0;
    } else if (dow === ((dayAfterWeekend + 1) % 7)) {
      // Two days after weekend — take 1 bridge day
      // e.g., Tue for Sat-Sun, Mon for Fri-Sat
      startDate = addDays(d, -3);
      endDate = d;
      bridgeDays = [toDateString(addDays(d, -1))];
      leaveDaysNeeded = 1;
    } else if (dow === ((dayBeforeWeekend - 1 + 7) % 7)) {
      // Two days before weekend — take 1 bridge day
      // e.g., Thu for Sat-Sun, Wed for Fri-Sat
      startDate = d;
      endDate = addDays(d, 3);
      bridgeDays = [toDateString(addDays(d, 1))];
      leaveDaysNeeded = 1;
    } else {
      // Mid-week (3 workdays from either weekend) — take 2 bridge days toward nearest weekend
      // e.g., Wed for Sat-Sun, Tue for Fri-Sat
      startDate = d;
      endDate = addDays(d, 4);
      bridgeDays = [toDateString(addDays(d, 1)), toDateString(addDays(d, 2))];
      leaveDaysNeeded = 2;
    }

    // Bridge days that are themselves public holidays are free, not leave. (almanak-omjj)
    bridgeDays = bridgeDays.filter(bd => !holidayDates.has(bd));
    leaveDaysNeeded = bridgeDays.length;

    const totalDaysOff = daysBetween(toDateString(startDate), toDateString(endDate)) + 1;

    results.push({
      holiday,
      totalDaysOff,
      leaveDaysNeeded,
      startDate: toDateString(startDate),
      endDate: toDateString(endDate),
      bridgeDays,
    });
  }

  // Sort by date
  results.sort((a, b) => a.startDate.localeCompare(b.startDate));

  // Dedup long weekends spanning the exact same dates — duplicate holiday records
  // (e.g. National Day (Merdeka) ≡ Independence Day) or double-renders. (almanak-24q3)
  const seenSpan = new Set<string>();
  return results.filter(r => {
    const key = `${r.startDate}_${r.endDate}`;
    if (seenSpan.has(key)) return false;
    seenSpan.add(key);
    return true;
  });
}

/** Get long weekends for a specific country */
export function getLongWeekendsForCountry(code: CountryCode): LongWeekend[] {
  const events = getEventsForCountry(code);
  return getLongWeekends(events, code);
}

/** Map country name to CountryCode (used to build year-suffixed slugs) */
const COUNTRY_NAME_MAP: Record<string, CountryCode> = {
  indonesia: 'ID',
  malaysia: 'MY',
  singapore: 'SG',
  myanmar: 'MM',
  afghanistan: 'AF',
  tibet: 'XR',
  india: 'IN',
  ethiopia: 'ET',
  mongolia: 'MN',
  laos: 'LA',
  sudan: 'SD',
  morocco: 'MA',
  seychelles: 'SC',
  mauritius: 'MU',
  tunisia: 'TN',
  madagascar: 'MG',
  rwanda: 'RW',
  senegal: 'SN',
  zimbabwe: 'ZW',
  ghana: 'GH',
  zambia: 'ZM',
  mozambique: 'MZ',
  malawi: 'MW',
  angola: 'AO',
  mali: 'ML',
  'burkina faso': 'BF',
  niger: 'NE',
  "cote d'ivoire": 'CI',
  'ivory coast': 'CI',
  chad: 'TD',
  cameroon: 'CM',
  burundi: 'BI',
  benin: 'BJ',
  guinea: 'GN',
  somalia: 'SO',
  liberia: 'LR',
  gabon: 'GA',
  'cabo verde': 'CV',
  'sierra leone': 'SL',
  botswana: 'BW',
  namibia: 'NA',
  togo: 'TG',
  gambia: 'GM',
  lesotho: 'LS',
  eswatini: 'SZ',
  djibouti: 'DJ',
  mauritania: 'MR',
  'south sudan': 'SS',
  eritrea: 'ER',
  'guinea-bissau': 'GW',
  'equatorial guinea': 'GQ',
  libya: 'LY',
  'central african republic': 'CF',
  congo: 'CG',
  comoros: 'KM',
  'saint helena': 'SH',
  'sao tome and principe': 'ST',
  mayotte: 'YT',
  'western sahara': 'EH',
  reunion: 'RE',
  yemen: 'YE',
  syria: 'SY',
  iraq: 'IQ',
  turkey: 'TR',
  'united-arab-emirates': 'AE',
  philippines: 'PH',
  thailand: 'TH',
  vietnam: 'VN',
};

/** Build slug like "indonesia-long-weekends-2026" */
function buildLwSlug(countryName: string): string {
  return `${countryName}-long-weekends-${YEAR}`;
}

export function getCountryCodeFromSlug(slug: string): CountryCode | null {
  // Try year-suffixed format: "indonesia-long-weekends-2026"
  const match = slug.match(/^(.+)-long-weekends-\d{4}$/);
  if (match) {
    return COUNTRY_NAME_MAP[match[1]] || null;
  }
  return null;
}

export function getAllLongWeekendSlugs(): string[] {
  return Object.keys(COUNTRY_NAME_MAP).map(name => buildLwSlug(name));
}

export function getCountrySlugFromCode(code: CountryCode): string | null {
  for (const [name, c] of Object.entries(COUNTRY_NAME_MAP)) {
    if (c === code) return buildLwSlug(name);
  }
  return null;
}

/** Build a map from event ID to its LongWeekend metadata */
export function getLongWeekendMap(events: CalendarEvent[]): Map<string, LongWeekend> {
  const lws = getLongWeekends(events);
  return new Map(lws.map(lw => [lw.holiday.id, lw]));
}

export type LongWeekendDayType = 'holiday' | 'bridge' | 'weekend';

/** Expand long weekend ranges into a per-date type lookup */
export function buildLongWeekendHighlights(
  events: CalendarEvent[],
  subType?: 'natural' | 'all'
): Map<string, LongWeekendDayType> {
  let lws = getLongWeekends(events);
  if (subType === 'natural') {
    lws = lws.filter(lw => lw.leaveDaysNeeded === 0);
  }
  const map = new Map<string, LongWeekendDayType>();
  const bridgeSet = new Set<string>();
  for (const lw of lws) {
    for (const bd of lw.bridgeDays) bridgeSet.add(bd);
  }

  for (const lw of lws) {
    const cur = new Date(lw.startDate + 'T00:00:00');
    const end = new Date(lw.endDate + 'T00:00:00');
    while (cur <= end) {
      const ds = toDateString(cur);
      if (!map.has(ds)) {
        map.set(ds, bridgeSet.has(ds) ? 'bridge' : ds === lw.holiday.dateStart ? 'holiday' : 'weekend');
      }
      cur.setDate(cur.getDate() + 1);
    }
  }
  return map;
}

export { COUNTRY_NAME_MAP };
